import WQAnalyteScatterPlot from '../../plots/WQAnalyteScatterPlot';
import GaugeDial from '../../plots/GaugeDial';
import { useAppStateContext } from '../../../hooks/useAppState';
import { useState, useEffect } from 'react';
import FadeTransition from '../../widgetsAndUtils/FadeTransition';

// Water Quality panel
export default function WQPerformancePanel({ analyteName }) {
  const { bmpState, getBmpTypeDisplayName, state } = useAppStateContext();
  const analysistype = bmpState.analysistype;
  // Use analyteName to get the correct data from global state
  const analyteData = analyteName ? state.wq.indexData?.[analyteName] : null;
  const plot_data = analyteData?.plot_data || [];
  const index_summary = analyteData?.index_summary || [];
  const comparisonMode = state.wq.comparisonMode;
  const scatterPlotMargin = state.wq.scatterPlotMargin || { top: 30, right: 20, bottom: 40, left: 50 };
  
  // Single state for comparison gauge visibility
  const [showComparison, setShowComparison] = useState(false);
  
  // Find selected and comparison BMPs
  const selectedBmpData = Array.isArray(index_summary)
    ? index_summary.find(d => typeof d.bmp === 'string' && !d.bmp.startsWith('Other'))
    : null;
  const comparisonBmpData = Array.isArray(index_summary)
    ? index_summary.find(d => typeof d.bmp === 'string' && d.bmp.startsWith('Other'))
    : null;

  // Handle comparison gauge visibility
  useEffect(() => {
    setShowComparison(analysistype === 'individual' && comparisonMode && comparisonBmpData);
  }, [analysistype, comparisonMode, comparisonBmpData]);

  // Get threshold for this analyte
  const analyteConfig = state.wq.selectedAnalytes.find(a => a.analytename === analyteName);
  const threshold = analyteConfig?.threshold;

  const getSummaryKeys = (data) => {
    if (!data) return [];
    return Object.keys(data).filter(key => 
      key !== 'bmp' && 
      key !== 'performance_index' && 
      key.startsWith('n_')
    );
  };


  const formatMetricName = (key) => {
    return key.replace('n_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getMetricColor = (key) => {
    const colors = {
      'n_success': 'bg-green-100 text-green-800 border-green-200',
      'n_excess': 'bg-blue-100 text-blue-800 border-blue-200',
      'n_marginal': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'n_insufficient': 'bg-orange-100 text-orange-800 border-orange-200',
      'n_failure': 'bg-red-100 text-red-800 border-red-200',
      'n_events_total': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[key] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Only filter for comparison if analysistype is 'individual'
  const filteredPlotData = Array.isArray(plot_data)
    ? (analysistype === 'individual'
        ? (comparisonMode ? plot_data : plot_data.filter(d => d.is_comparison !== 'true'))
        : plot_data)
    : plot_data;

  

  return (
    <div className="flex flex-col gap-8 my-8 w-full min-h-[440px] py-8">
      {/* Analyte Name and Threshold Header */}
      <div className="mb-2">
        <h3 className="text-lg font-semibold mb-1">{analyteName}</h3>
        {threshold !== undefined && (
          <p className="text-base font-normal text-gray-600">
            Threshold: {threshold}
          </p>
        )}
      </div>
      {/* Top: Scatter Plot and Gauge (side by side) */}
      <div className="flex flex-col md:flex-row gap-8 w-full">
        {/* Left: Scatter Plot (50%) */}
        <div className="w-full md:w-1/2">
          <WQAnalyteScatterPlot data={filteredPlotData} />
        </div>
        
        {/* Right: Gauge (50%) - vertically centered */}
        <div className="w-full md:w-1/2 flex flex-col items-center justify-center" style={{ paddingBottom: scatterPlotMargin.bottom, minHeight: 400 }}>
          <div className="mb-6 w-full flex flex-col items-center justify-center">
            <h4 className="text-md font-semibold mb-2 text-center">{analysistype === 'bmptype' && selectedBmpData?.bmp ? getBmpTypeDisplayName(selectedBmpData.bmp) || selectedBmpData.bmp : selectedBmpData?.bmp || "Selected BMP"}</h4>
            <GaugeDial value={selectedBmpData?.performance_index ?? 0} />
          </div>
          
          <FadeTransition
            in={showComparison}
            timeout={500}
            unmountOnExit
            className="w-full flex flex-col items-center justify-center"
          >
            <h4 className="text-md font-semibold mb-2 text-center">{analysistype === 'bmptype' && comparisonBmpData?.bmp ? getBmpTypeDisplayName(comparisonBmpData.bmp) || comparisonBmpData.bmp : comparisonBmpData?.bmp || "Other BMPs of the same type"}</h4>
            <GaugeDial value={comparisonBmpData?.performance_index ?? 0} />
          </FadeTransition>
        </div>
      </div>

      {/* Bottom: Summary Statistics Cards */}
      <div className="w-full">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Performance Summary</h3>
        
        {/* Selected BMP Summary */}
        <div className="mb-6">
          <h4 className="text-md font-medium mb-3 text-gray-700">{analysistype === 'bmptype' && selectedBmpData?.bmp ? getBmpTypeDisplayName(selectedBmpData.bmp) || selectedBmpData.bmp : selectedBmpData?.bmp || "Selected BMP"}</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {getSummaryKeys(selectedBmpData).map(key => (
              <div key={key} className={`p-4 rounded-lg border ${getMetricColor(key)}`}>
                <div className="text-2xl font-bold">{selectedBmpData[key]}</div>
                <div className="text-sm opacity-80">{formatMetricName(key)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Comparison BMP Summary (only show when comparisonMode is true and comparison data exists) */}
        {analysistype === 'individual' && comparisonMode && comparisonBmpData && (
          <div>
            <h4 className="text-md font-medium mb-3 text-gray-700">{analysistype === 'bmptype' && comparisonBmpData?.bmp ? getBmpTypeDisplayName(comparisonBmpData.bmp) || comparisonBmpData.bmp : comparisonBmpData?.bmp || "Other BMPs of the same type"}</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {getSummaryKeys(comparisonBmpData).map(key => (
                <div key={key} className={`p-4 rounded-lg border ${getMetricColor(key)}`}>
                  <div className="text-2xl font-bold">{comparisonBmpData[key]}</div>
                  <div className="text-sm opacity-80">{formatMetricName(key)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 